import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { LOGO_URL } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setAmount(res.data.amount);
          setStatus("paid");
          return;
        }
      } catch (err) {
        // keep polling
      }
      attempts += 1;
      if (attempts < 15) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <section className="min-h-[70vh] flex items-center bg-[#0A0A0A]" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <img src={LOGO_URL} alt="Louisiana Ducks logo" className="h-24 w-24 object-contain mx-auto mb-8" />
        {status === "checking" && (
          <div data-testid="payment-status-checking">
            <Loader2 size={48} className="text-[#FEE123] animate-spin mx-auto" />
            <h1 className="font-display text-4xl uppercase text-white mt-6">Confirming Your Donation</h1>
            <p className="mt-4 text-gray-400">Hold tight — we're verifying your payment with Stripe.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-status-paid">
            <CheckCircle2 size={56} className="text-[#FEE123] mx-auto" />
            <h1 className="font-display text-5xl uppercase text-white mt-6">
              Thank You{amount ? ` — $${(amount / 100).toFixed(0)} Donated` : ""}
            </h1>
            <p className="mt-5 text-gray-300 leading-relaxed">
              Your donation directly supports scholarships, tutoring, mentorship, and college exposure for young
              athletes across the New Orleans metro area. Go Ducks.
            </p>
          </div>
        )}
        {(status === "timeout" || status === "error") && (
          <div data-testid="payment-status-pending">
            <h1 className="font-display text-4xl uppercase text-white mt-6">Almost There</h1>
            <p className="mt-4 text-gray-400">
              We're still confirming your payment. If you completed checkout, your donation went through — check your
              email for a receipt.
            </p>
          </div>
        )}
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="mt-10 inline-block bg-[#0F4D22] text-white text-base font-bold uppercase tracking-widest px-8 py-3 rounded-sm border border-[#FEE123]/30 transition-colors duration-200 hover:bg-[#146B2F]"
        >
          Back To Home
        </Link>
      </div>
    </section>
  );
}
